import React, { useState, useEffect } from "react";
import { Box, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import { Carousel } from "react-responsive-carousel";
import { useHistory } from "react-router-dom";
import ApiService from "./../../services/api-service";
import Loader from "./../../components/Loader";
import VideoBanner from "./VideoBanner";
import { isInternalUrl, getInternalPath, isDynamicLink, handleLinkClick } from "../../utils/urlUtils";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import "./index.scss";

// Extract styles
const styles = {
  container: {
    width: "100%",
    // match .siki-home-content gutters so the banner edges line up with the rows below
    padding: { xs: "0 16px 16px", md: "0 24px 20px" },
    display: "flex",
    justifyContent: "center",
  },
  innerBox: {
    width: "100%",
    // 1232px = .siki-home-content's 1280px max-width minus its 24px side padding
    maxWidth: "1232px",
    display: "flex",
    justifyContent: "space-between",
    gap: "20px",
  },
  bannerBox: {
    width: "100%",
    aspectRatio: "1072 / 325",
    borderRadius: "16px",
    overflow: "hidden",
    background: "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(0,0,0,0.04))",
    position: "relative",
  },
  arrow: {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    zIndex: 1,
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    transition: "background-color 0.2s ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.7)",
    },
  },
  videoBox: {
    width: "100%",
    height: "100%",
    borderRadius: "16px",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "linear-gradient(135deg, rgba(255,255,255,0.04), rgba(0,0,0,0.04))",
    flex: 1,
    "&:hover": {
      transform: "scale(1.02)",
      transition: "transform 0.3s ease-in-out",
    },
  },
};

// Extract components
const CarouselArrow = ({ direction, onClick }) => (
  <Box
    sx={{ ...styles.arrow, [direction]: "12px" }}
    onClick={onClick}
  >
    {direction === "left" ? (
      <ArrowBackIosNewIcon sx={{ fontSize: "16px", color: "white", ml: "2px" }} />
    ) : (
      <ArrowForwardIosIcon sx={{ fontSize: "16px", color: "white", ml: "1px" }} />
    )}
  </Box>
);

const BannerSlide = ({ banner, history }) => {
  const handleClick = async (e) => {
    e.preventDefault();
    const url = banner.url;
    if (!url || url === "#") return;

    // Use the unified link handler for all URL types
    await handleLinkClick(url, history);
  };

  // Determine if link should open in same tab (internal or dynamic link)
  const isInternal = isInternalUrl(banner.url) || isDynamicLink(banner.url);

  return (
    <Box
      sx={{
        overflow: "hidden",
        borderRadius: "16px",
        "& img": {
          width: "100%",
          height: "100%",
          objectFit: "cover",
          transition: "transform 0.3s ease",
        },
        "&:hover img": {
          transform: "scale(1.05)",
        },
      }}
    >
      <a
        href={banner.url || "#"}
        target={isInternal ? "_self" : "_blank"}
        rel="noopener noreferrer"
        onClick={handleClick}
        style={{ display: "block", width: "100%", height: "100%", cursor: "pointer" }}
      >
        <img src={banner.image?.filePath || ""} alt="Banner" />
      </a>
    </Box>
  );
};

export default function TopBan() {
  const [banner, setBanner] = useState([]);
  const [loading, setLoading] = useState(false);
  const history = useHistory();

  // Remove a single broken banner; fall back to test banners if none left
  const handleBannerError = (failedId) => {
    setBanner(prev => {
      const remaining = prev.filter(b => (b.uniqueId || b._id || b.id) !== failedId);
      return remaining.length > 0 ? remaining : testBanners;
    });
  };

  // Create test data with colorful placeholders
  const testBanners = [
    { 
      id: 1,
      color: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
      title: "Featured Music",
      subtitle: "Discover new artists",
      url: "#"
    },
    { 
      id: 2,
      color: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)",
      title: "New Releases",
      subtitle: "Fresh tracks daily",
      url: "#"
    },
    { 
      id: 3,
      color: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)",
      title: "Trending Now",
      subtitle: "What's hot today",
      url: "#"
    },
    { 
      id: 4,
      color: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)",
      title: "Top Charts",
      subtitle: "Most played songs",
      url: "#"
    },
  ];

  const getBanner = async () => {
    try {
      setLoading(true);
      // Fetch actual banners from API
      const response = await ApiService.GET_MEDIA_BANNERS();
      if (response.result && response.result.data && response.result.data.length > 0) {
        const webBanners = response.result.data.filter(
          banner => !banner.isMobile && banner.image?.filePath
        );
        setBanner(webBanners.length > 0 ? webBanners : testBanners);
      } else {
        // Fall back to test banners if no uploaded banners
        setBanner(testBanners);
      }
      setLoading(false);
    } catch (error) {
      console.error("Error fetching banners:", error);
      // Fall back to test banners on error
      setBanner(testBanners);
      setLoading(false);
    }
  };

  useEffect(() => {
    getBanner();
  }, []);

  return (
    <Box sx={styles.container}>
      <Box sx={styles.innerBox}>
        {/* Left Side: Carousel Banner */}
        <Box sx={{
          ...styles.bannerBox,
        }}>
          {loading ? (
            <Box sx={{
              width: "100%",
              aspectRatio: "1072 / 325",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <Loader />
            </Box>
          ) : (
            <Carousel
              showThumbs={false}
              autoPlay
              interval={6000}
              transitionTime={600}
              infiniteLoop
              showArrows
              showStatus={false}
              renderArrowPrev={(onClick, hasPrev) =>
                hasPrev && <CarouselArrow direction="left" onClick={onClick} />
              }
              renderArrowNext={(onClick, hasNext) =>
                hasNext && <CarouselArrow direction="right" onClick={onClick} />
              }
            >
              {banner.map((b, index) => (
                <Box
                  key={b.uniqueId || b.id || `banner-${index}`}
                  component={b.url ? "a" : "div"}
                  href={b.url || "#"}
                  target={(isInternalUrl(b.url) || isDynamicLink(b.url)) ? "_self" : "_blank"}
                  rel="noopener noreferrer"
                  onClick={async (e) => {
                    if (b.url) {
                      e.preventDefault();
                      await handleLinkClick(b.url, history);
                    }
                  }}
                  sx={{
                    width: "100%",
                    aspectRatio: "1072 / 325",
                    background: b.color || "transparent",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "white",
                    borderRadius: "16px",
                    position: "relative",
                    overflow: "hidden",
                    cursor: "pointer",
                    textDecoration: "none",
                    "&:hover": {
                      "& .banner-content": {
                        transform: "translateY(-5px)",
                      }
                    }
                  }}
                >
                  {b.image?.filePath ? (
                    // Check if it's a video or image
                    b.image.filePath.endsWith(".mp4") ||
                    b.image.filePath.endsWith(".webm") ||
                    b.image.filePath.endsWith(".mov") ? (
                      <video
                        src={b.image.filePath}
                        autoPlay
                        loop
                        muted
                        playsInline
                        disablePictureInPicture
                        onError={() => handleBannerError(b.uniqueId || b._id || b.id)}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          position: "absolute",
                          top: 0,
                          left: 0,
                        }}
                      />
                    ) : (
                      <img
                        src={b.image.filePath}
                        alt="Banner"
                        onError={() => handleBannerError(b.uniqueId || b._id || b.id)}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "contain",
                          position: "absolute",
                          top: 0,
                          left: 0,
                        }}
                      />
                    )
                  ) : (
                    <Box 
                      className="banner-content"
                      sx={{ 
                        zIndex: 1, 
                        textAlign: "center",
                        transition: "transform 0.3s ease",
                      }}
                    >
                      <Typography 
                        variant="h4" // Reduced from h3
                        sx={{ 
                          fontWeight: "bold", 
                          mb: 1, // Reduced from 2
                          textShadow: "2px 2px 4px rgba(0,0,0,0.3)"
                        }}
                      >
                        {b.title}
                      </Typography>
                      <Typography 
                        variant="body1" // Reduced from h6
                        sx={{ 
                          opacity: 0.9,
                          textShadow: "1px 1px 2px rgba(0,0,0,0.3)"
                        }}
                      >
                        {b.subtitle}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ))}
            </Carousel>
          )}
        </Box>

        {/* Right Side: Video Banner */}
        <Box sx={{
          ...styles.videoBox,
          minHeight: "250px", // Reduced from 400px
          maxHeight: "250px", // Add max height
          display: "none", // Hide video banner to show left banner across whole width
        }}>
          <VideoBanner />
        </Box>
      </Box>
    </Box>
  );
}